import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiChevronDown,
  FiFilter,
  FiX,
} from "react-icons/fi";

import {
  Badge,
  BandList,
  BandName,
  ClearFilters,
  CountPill,
  Ellipsis,
  FilterSection,
  FilterTitle,
  GenreButton,
  LoadingWrap,
  Main,
  NavBtn,
  NoResults,
  PageBtn,
  Pagination,
  ProductCard,
  ProductsGrid,
  ProductTitle,
  SearchIcon,
  SearchInput,
  SearchWrap,
  Section,
  SectionHeader,
  SectionTitle,
  Sidebar,
  SortSelect,
  Spinner,
  ViewOptions,
  ProductImageContainer,
  ProductHeader,
  ProductBand,
  ProductMeta,
  ProductMetaItem,
  ProductCategory,
  PriceContainer,
  ProductPrice,
  ProductDiscountPrice,
  ProductShortDesc,
  ProductImage,
  FilterButton,
  MobileFilterModal,
  MobileFilterOverlay,
  MobileFilterContent,
  MobileFilterHeader,
  MobileFilterTitle,
  MobileFilterClose,
  MobileFilterBody,
} from "./ShopPage.styles";

import Particles from "../../../components/utils/Particles";
import MainContainer from "../../../components/utils/MainContainer";
import { formatPrice, getBadgeLabel, getVariant } from "../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { useScrollLock } from "../../../hooks/useScrollLock";
import {
  selectCategories,
  selectProducts,
} from "../../../store/slices/products/slice";
import { getProducts } from "../../../store/slices/products/asyncThunks";
import { selectBandsDropdown } from "../../../store/slices/bands/slice";
import { trackBandView } from "../../../store/slices/viewTracking/asyncThunks";
import { useNavigate, useLocation } from "react-router-dom";

function Dropdown({ title, isOpen, onToggle, children }) {
  return (
    <FilterSection>
      <FilterTitle
        onClick={onToggle}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {title}
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </FilterTitle>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </FilterSection>
  );
}

export default function ShopPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);
  const categories = useSelector(selectCategories);
  const { data: bandsData, loading: bandsLoading } =
    useSelector(selectBandsDropdown);

  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [bandFilter, setBandFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [initialFiltersSet, setInitialFiltersSet] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const closeMobileFilter = () => {
    setIsMobileFilterOpen(false);
  };

  // Block scrolling when mobile filter modal is open
  useScrollLock(isMobileFilterOpen);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // : Handle URL parametre za bend i kategoriju
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bandId = params.get("bend");
    const categoryId = params.get("kategorija");

    if ((bandId || categoryId) && !initialFiltersSet) {
      let filtersSet = false;

      // Handle bend parametar
      if (bandId && bandsData && Array.isArray(bandsData)) {
        const foundBand = bandsData.find(
          (band) => band.id?.toString() === bandId
        );

        if (foundBand) {
          setBandFilter(foundBand.name);
          setOpenDropdown("bands");
          filtersSet = true;
        }
      }

      // Handle kategorija parametar
      if (categoryId && categories?.data && Array.isArray(categories.data)) {
        const foundCategory = categories.data.find(
          (cat) => cat.id?.toString() === categoryId
        );

        if (foundCategory) {
          setCategoryFilter(foundCategory.id); // : koristi ID umesto imena
          if (!filtersSet) {
            // Otvori categories dropdown samo ako bendovi nije već otvoren
            setOpenDropdown("categories");
          }
          filtersSet = true;
        }
      }

      if (filtersSet) {
        setInitialFiltersSet(true);
      }
    }
  }, [location.search, bandsData, categories?.data, initialFiltersSet]);

  useEffect(() => {
    dispatch(getProducts({ page: 1 }));
  }, [dispatch]);

  const productsPerPage = 12;

  const visibleBands = useMemo(() => {
    if (!bandsData || !Array.isArray(bandsData) || !products?.data) return [];

    // Ako je categoryFilter "all", prikaži sve bendove
    if (categoryFilter === "all") {
      return bandsData
        .map((band) => band.name)
        .sort((a, b) => a.localeCompare(b));
    }

    // Inače, prikaži samo bendove koji imaju proizvode u trenutnoj kategoriji
    const bandsWithProductsInCategory = new Set();

    products.data.forEach((product) => {
      // : poredi po ID-u kategorije
      if (product.category?.id?.toString() === categoryFilter.toString()) {
        const bandName =
          typeof product.band === "object" ? product.band.name : product.band;
        if (bandName) {
          bandsWithProductsInCategory.add(bandName);
        }
      }
    });

    return bandsData
      .filter((band) => bandsWithProductsInCategory.has(band.name))
      .map((band) => band.name)
      .sort((a, b) => a.localeCompare(b));
  }, [bandsData, categoryFilter, products?.data]);

  const productCountsByBand = useMemo(() => {
    const map = new Map();
    if (!products?.data) return map;

    for (const p of products.data) {
      // Filter po trenutnoj kategoriji ako nije "all"
      if (categoryFilter !== "all") {
        if (p.category?.id?.toString() !== categoryFilter.toString()) {
          continue; // Preskači proizvode koji nisu u trenutnoj kategoriji
        }
      }

      const bandName = typeof p.band === "object" ? p.band.name : p.band;
      if (bandName) {
        map.set(bandName, (map.get(bandName) || 0) + 1);
      }
    }
    return map;
  }, [products, categoryFilter]);

  // Filtering + search - GLAVNA
  useEffect(() => {
    if (!products?.data) {
      setFiltered([]);
      return;
    }

    let data = products.data.filter((p) => {
      // Band filter
      const productBandName =
        typeof p.band === "object" ? p.band?.name : p.band;
      const bandMatch = !bandFilter || productBandName === bandFilter;

      // Category filter - : poredi po ID-u kategorije
      let categoryMatch = categoryFilter === "all";
      if (!categoryMatch) {
        categoryMatch =
          p.category?.id?.toString() === categoryFilter.toString();
      }

      // Search - naziv proizvoda i benda
      const q = debouncedSearch.trim().toLowerCase();
      const searchMatch =
        !q ||
        p?.name?.toLowerCase().includes(q) ||
        productBandName?.toLowerCase().includes(q);

      return bandMatch && categoryMatch && searchMatch;
    });

    // Enhanced Sort - Better price handling
    if (sortBy === "name") {
      data = data.sort((a, b) => {
        const nameA = a?.name?.toLowerCase() || "";
        const nameB = b?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB, "sr-Latn-RS");
      });
    } else if (sortBy === "price-low") {
      data = data.sort((a, b) => {
        const priceA = a?.salePrice || a?.price || 0;
        const priceB = b?.salePrice || b?.price || 0;
        return priceA - priceB;
      });
    } else if (sortBy === "price-high") {
      data = data.sort((a, b) => {
        const priceA = a?.salePrice || a?.price || 0;
        const priceB = b?.salePrice || b?.price || 0;
        return priceB - priceA;
      });
    } else if (sortBy === "newest") {
      data = data.sort((a, b) => {
        const dateA = new Date(a?.createdAt || a?.id || 0);
        const dateB = new Date(b?.createdAt || b?.id || 0);
        return dateB - dateA;
      });
    }

    setFiltered(data);
    setCurrentPage(1);
  }, [products, bandFilter, categoryFilter, debouncedSearch, sortBy]);

  const totalPages = Math.ceil(filtered.length / productsPerPage) || 1;
  const start = (currentPage - 1) * productsPerPage;
  const end = start + productsPerPage;
  const pageItems = filtered.slice(start, end);

  const sectionTitle = useMemo(() => {
    let title = "Svi proizvodi";
    if (bandFilter) {
      title = `${bandFilter} — proizvodi`;
    } else if (categoryFilter !== "all") {
      // Pronađi ime kategorije po ID-u
      const categoryName = categories?.data?.find(
        (cat) => cat.id?.toString() === categoryFilter.toString()
      )?.name;
      if (categoryName) {
        title = `${categoryName} — svi proizvodi`;
      }
    }
    return `${title} (${filtered.length})`;
  }, [bandFilter, categoryFilter, filtered.length, categories?.data]);

  // Pagination builder with ellipsis (smart)
  function renderPaginationBtns() {
    const btns = [];

    const pushPage = (n) =>
      btns.push(
        <PageBtn
          key={`p-${n}`}
          $active={n === currentPage}
          onClick={() => {
            setCurrentPage(n);
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 300);
          }}
        >
          {n}
        </PageBtn>
      );

    // Prev
    btns.push(
      <NavBtn
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
      >
        <FiChevronLeft /> Prethodna
      </NavBtn>
    );

    // Always show 1
    pushPage(1);

    const delta = 2;
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    if (left > 2) btns.push(<Ellipsis key="el1">…</Ellipsis>);

    for (let i = left; i <= right; i++) {
      if (i > 1 && i < totalPages) pushPage(i);
    }

    if (right < totalPages - 1) btns.push(<Ellipsis key="el2">…</Ellipsis>);

    if (totalPages > 1) pushPage(totalPages);

    // Next
    btns.push(
      <NavBtn
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
      >
        Sledeća <FiChevronRight />
      </NavBtn>
    );

    return btns;
  }

  // Handlers
  const handleCategoryClick = (categoryId) => {
    setCategoryFilter(categoryId);
    setBandFilter(null);

    // Ažuriraj URL parametre
    const params = new URLSearchParams(location.search);

    // Ukloni bend parametar kada se menja kategorija
    params.delete("bend");

    if (categoryId !== "all") {
      params.set("kategorija", categoryId.toString());
    } else {
      params.delete("kategorija");
    }

    const newSearch = params.toString();
    const newUrl = newSearch
      ? `${location.pathname}?${newSearch}`
      : location.pathname;
    navigate(newUrl, { replace: true });
  };

  const handleBandClick = (name) => {
    const newBandFilter = name === "__all__" ? null : name;
    setBandFilter(newBandFilter);

    // Ažuriraj URL parametre
    const params = new URLSearchParams(location.search);

    if (newBandFilter && bandsData) {
      // Pronađi ID benda po imenu
      const foundBand = bandsData.find((band) => band.name === newBandFilter);
      if (foundBand) {
        params.set("bend", foundBand.id.toString());
        // Track band view when selected in filter
        dispatch(trackBandView(foundBand.id));
      }
    } else {
      params.delete("bend");
    }

    const newSearch = params.toString();
    const newUrl = newSearch
      ? `${location.pathname}?${newSearch}`
      : location.pathname;
    navigate(newUrl, { replace: true });
  };

  const handleClear = () => {
    setCategoryFilter("all");
    setBandFilter(null);
    setSearch("");
    setDebouncedSearch("");
    setSortBy("name");
    setCurrentPage(1);

    // Očisti sve URL parametre
    navigate(location.pathname, { replace: true });
  };

  // DODAVANJE LOADING CHECK-a
  if (products?.loading) {
    return (
      <MainContainer>
        <Particles />

        <LoadingWrap>
          <l-line-spinner size="20" stroke="2" speed="1" color="#fff" />
          Učitavanje proizvoda...
        </LoadingWrap>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Main>
        <Particles />

        <Sidebar>
          {/* Kategorije */}
          <Dropdown
            title="Kategorije"
            isOpen={openDropdown === "categories"}
            onToggle={() =>
              setOpenDropdown(
                openDropdown === "categories" ? null : "categories"
              )
            }
          >
            <BandList>
              <GenreButton
                $active={categoryFilter === "all"}
                onClick={() => handleCategoryClick("all")}
              >
                Sve kategorije
              </GenreButton>
              {categories?.data
                ?.filter((category) => category.active)
                .map((category) => (
                  <GenreButton
                    key={category.id}
                    $active={
                      categoryFilter.toString() === category.id.toString()
                    }
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    {category?.name}
                  </GenreButton>
                ))}
            </BandList>
          </Dropdown>

          {/* Bendovi */}
          <Dropdown
            title="Bendovi"
            isOpen={openDropdown === "bands"}
            onToggle={() =>
              setOpenDropdown(openDropdown === "bands" ? null : "bands")
            }
          >
            {bandsLoading ? (
              <LoadingWrap>
                <Spinner />
                Učitavanje bendova...
              </LoadingWrap>
            ) : (
              <BandList>
                <GenreButton
                  $active={!bandFilter}
                  onClick={() => handleBandClick("__all__")}
                >
                  <BandName>Svi bendovi</BandName>
                  <CountPill>{products?.data?.length || 0}</CountPill>
                </GenreButton>
                {visibleBands?.map((band) => (
                  <GenreButton
                    key={band}
                    $active={bandFilter === band}
                    onClick={() => handleBandClick(band)}
                  >
                    <BandName>{band}</BandName>
                    <CountPill>{productCountsByBand?.get(band) || 0}</CountPill>
                  </GenreButton>
                ))}
              </BandList>
            )}
            <ClearFilters onClick={handleClear}>Očisti filtere</ClearFilters>
          </Dropdown>
        </Sidebar>

        <Section>
          <SectionHeader>
            <SectionTitle>{sectionTitle}</SectionTitle>
            <ViewOptions>
              <FilterButton onClick={toggleMobileFilter}>
                <FiFilter />
                Filteri
              </FilterButton>
              <SearchWrap>
                <SearchInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Pretraga proizvoda po nazivu..."
                  aria-label="Pretraga"
                />
                <SearchIcon />
              </SearchWrap>
              <SortSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                disabled={products?.loading}
              >
                <option value="name">Sortiranje po imenu</option>
                <option value="price-low">Cena: manje → više</option>
                <option value="price-high">Cena: više → manje</option>
                <option value="newest">Najnoviji</option>
              </SortSelect>
            </ViewOptions>
          </SectionHeader>

          {pageItems.length === 0 ? (
            <NoResults>
              <h3>Nema rezultata</h3>
              <p>Pokušaj drugi filter ili pretragu.</p>
            </NoResults>
          ) : (
            <ProductsGrid>
              {pageItems?.map((product) => (
                <ProductCard
                  key={product.id}
                  onClick={() => {
                    navigate(`/proizvod/${product.id}`);
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  <ProductImageContainer>
                    <ProductImage
                      src={
                        product.mainImage
                          ? product.mainImage
                          : "/placeholder.jpg"
                      }
                      alt={product.name}
                    />
                    {getVariant(product) && (
                      <Badge $variant={getVariant(product)}>
                        {getBadgeLabel(product)}
                      </Badge>
                    )}
                  </ProductImageContainer>

                  <ProductHeader>
                    <ProductTitle>{product?.name}</ProductTitle>
                    <ProductBand>
                      {typeof product?.band === "object"
                        ? product?.band?.name
                        : product?.band}
                    </ProductBand>
                  </ProductHeader>

                  <ProductShortDesc>{product?.description}</ProductShortDesc>

                  <ProductMeta>
                    <ProductMetaItem>
                      <ProductCategory>
                        {product?.category?.name}
                      </ProductCategory>
                    </ProductMetaItem>

                    <ProductMetaItem>
                      <PriceContainer>
                        {product?.salePrice ? (
                          <>
                            <ProductDiscountPrice>
                              {formatPrice(product?.salePrice)}
                            </ProductDiscountPrice>
                            <ProductPrice $discounted>
                              {formatPrice(product?.price)}
                            </ProductPrice>
                          </>
                        ) : (
                          <ProductPrice>
                            {formatPrice(product?.price)}
                          </ProductPrice>
                        )}
                      </PriceContainer>
                    </ProductMetaItem>
                  </ProductMeta>
                </ProductCard>
              ))}
            </ProductsGrid>
          )}

          <Pagination>{renderPaginationBtns()}</Pagination>
        </Section>

        {/* Mobile Filter Modal */}
        <MobileFilterModal $isOpen={isMobileFilterOpen}>
          <MobileFilterOverlay onClick={closeMobileFilter} />
          <MobileFilterContent>
            <MobileFilterHeader>
              <MobileFilterTitle>Filteri</MobileFilterTitle>
              <MobileFilterClose onClick={closeMobileFilter}>
                <FiX />
              </MobileFilterClose>
            </MobileFilterHeader>

            <MobileFilterBody>
              {/* Kategorije */}
              <Dropdown
                title="Kategorije"
                isOpen={openDropdown === "categories"}
                onToggle={() =>
                  setOpenDropdown(
                    openDropdown === "categories" ? null : "categories"
                  )
                }
              >
                <BandList>
                  <GenreButton
                    $active={categoryFilter === "all"}
                    onClick={() => {
                      handleCategoryClick("all");
                      closeMobileFilter();
                    }}
                  >
                    Sve kategorije
                  </GenreButton>
                  {categories?.data
                    ?.filter((category) => category.active)
                    .map((category) => (
                      <GenreButton
                        key={category.id}
                        $active={
                          categoryFilter.toString() === category.id.toString()
                        }
                        onClick={() => {
                          handleCategoryClick(category.id);
                          closeMobileFilter();
                        }}
                      >
                        {category?.name}
                      </GenreButton>
                    ))}
                </BandList>
              </Dropdown>

              {/* Bendovi */}
              <Dropdown
                title="Bendovi"
                isOpen={openDropdown === "bands"}
                onToggle={() =>
                  setOpenDropdown(openDropdown === "bands" ? null : "bands")
                }
              >
                {bandsLoading ? (
                  <LoadingWrap>
                    <Spinner />
                    Učitavanje bendova...
                  </LoadingWrap>
                ) : (
                  <BandList>
                    <GenreButton
                      $active={!bandFilter}
                      onClick={() => {
                        handleBandClick("__all__");
                        closeMobileFilter();
                      }}
                    >
                      <BandName>Svi bendovi</BandName>
                      <CountPill>{products?.data?.length || 0}</CountPill>
                    </GenreButton>
                    {visibleBands?.map((band) => (
                      <GenreButton
                        key={band}
                        $active={bandFilter === band}
                        onClick={() => {
                          handleBandClick(band);
                          closeMobileFilter();
                        }}
                      >
                        <BandName>{band}</BandName>
                        <CountPill>
                          {productCountsByBand?.get(band) || 0}
                        </CountPill>
                      </GenreButton>
                    ))}
                  </BandList>
                )}
                <ClearFilters
                  onClick={() => {
                    handleClear();
                    closeMobileFilter();
                  }}
                >
                  Očisti filtere
                </ClearFilters>
              </Dropdown>
            </MobileFilterBody>
          </MobileFilterContent>
        </MobileFilterModal>
      </Main>
    </MainContainer>
  );
}
